import React, { useState } from 'react';
import { ShieldCheck, BadgeCheck, CalendarCheck, FileCheck, Maximize2, Factory } from 'lucide-react';
import { ImageLightbox } from './ImageLightbox';

const certImages = [{ image: '/iso-certificate.png', title: 'ISO 9001:2015 Certificate of Registration' }];

const certDetails = [
  {
    icon: BadgeCheck,
    label: 'Standard',
    value: 'ISO 9001:2015 — Quality Management System'
  },
  {
    icon: Factory,
    label: 'Certified Scope',
    value: 'Manufacture and repairing of vulcanizing machines and belt vice (frog clamps)'
  },
  {
    icon: FileCheck,
    label: 'Certificate Number',
    value: '3050260502121Q'
  },
  {
    icon: CalendarCheck,
    label: 'Validity',
    value: 'Certified since 2nd May 2026 · Valid until 1st May 2029'
  }];


export const Certification = () => {
  const [lightboxOpen, setLightboxOpen] = useState(false);

  return (
    <section id="certification" className="relative py-16 md:py-24 bg-slate-900 overflow-hidden">
      {/* Subtle background accents */}
      <div className="absolute inset-0 opacity-[0.06]" style={{
        backgroundImage: 'linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)',
        backgroundSize: '48px 48px'
      }}></div>
      <div className="absolute -top-32 -right-32 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl"></div>
      <div className="absolute -bottom-32 -left-32 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 bg-blue-600/15 border border-blue-500/30 text-blue-300 px-5 py-2 rounded-full text-sm font-semibold tracking-wide uppercase mb-5">
            <ShieldCheck size={16} />
            Certified Quality
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
            ISO 9001:2015 <span className="text-orange-500">Certified</span>
          </h2>
          <div className="w-24 h-1 bg-blue-600 mx-auto mb-5"></div>
          <p className="text-lg text-gray-300 max-w-3xl mx-auto">
            Our Quality Management System is independently assessed and certified, assuring you of consistent precision, reliability and process excellence in every machine we build.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Certificate Preview */}
          <div className="flex justify-center">
            <button
              onClick={() => setLightboxOpen(true)}
              data-testid="iso-certificate-preview"
              className="group relative rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label="View ISO 9001:2015 certificate full screen">

              <div className="absolute -inset-3 bg-gradient-to-br from-blue-500/40 via-transparent to-orange-500/30 rounded-xl blur-md opacity-60 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="relative bg-white p-2 rounded-lg shadow-2xl transform group-hover:scale-[1.02] transition-transform duration-500">
                <img
                  src="/iso-certificate.png"
                  alt="ISO 9001:2015 Certificate of Registration — Tek Solutions and Consultants"
                  className="w-64 sm:w-80 md:w-96 h-auto rounded"
                  loading="lazy" />

                <div className="absolute inset-0 flex items-center justify-center bg-slate-900/0 group-hover:bg-slate-900/40 rounded-lg transition-colors duration-300">
                  <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 inline-flex items-center gap-2 bg-blue-600 text-white px-5 py-2.5 rounded-full text-sm font-semibold shadow-lg">
                    <Maximize2 size={16} />
                    View Certificate
                  </span>
                </div>
              </div>
            </button>
          </div>

          {/* Details */}
          <div className="space-y-5">
            {certDetails.map((item, index) => {
              const Icon = item.icon;
              return (
                <div
                  key={index}
                  className="flex items-start gap-4 bg-white/5 border border-white/10 backdrop-blur-sm p-5 rounded-lg hover:bg-white/10 hover:border-blue-500/40 transition-all duration-300">

                  <div className="flex-shrink-0 w-11 h-11 rounded-lg bg-blue-600/20 border border-blue-500/30 flex items-center justify-center">
                    <Icon size={22} className="text-blue-400" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-orange-400 mb-1">{item.label}</p>
                    <p className="text-gray-200 leading-relaxed">{item.value}</p>
                  </div>
                </div>);

            })}

            <p className="text-sm text-gray-400 pt-1">
              Certified by QRO — Quality Research Organization · EGAC accredited, IAF MLA member
            </p>
          </div>
        </div>
      </div>

      {lightboxOpen &&
      <ImageLightbox
        images={certImages}
        currentIndex={0}
        onClose={() => setLightboxOpen(false)}
        onNext={() => {}}
        onPrev={() => {}} />
      }
    </section>);

};
